import { useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { MailX, Save, X } from "lucide-react";
import { getVzEmailExclusions, saveVzEmailExclusions } from "@/lib/verizonSalesApi";

export const EmailExclusionsPanel = () => {
  const [data, setData] = useState(null);
  const [sel, setSel] = useState([]);
  const [q, setQ] = useState("");
  useEffect(() => { getVzEmailExclusions().then((d) => { setData(d); setSel(d.excluded); }).catch(() => toast.error("Failed to load")); }, []);
  const matches = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!data || !t) return [];
    return data.stores.filter((s) => !sel.includes(s.store_id) && [s.store_id, s.address, s.city, s.sub_agent_name].join(" ").toLowerCase().includes(t)).slice(0, 8);
  }, [q, data, sel]);
  if (!data) return null;
  const byId = Object.fromEntries(data.stores.map((s) => [s.store_id, s]));
  const save = async () => {
    try { const r = await saveVzEmailExclusions(sel); setData({ ...data, excluded: r.excluded }); window.dispatchEvent(new Event("vz-exclusions-changed")); toast.success("Saved. These stores won't be in any email."); }
    catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  const dirty = JSON.stringify([...sel].sort()) !== JSON.stringify(data.excluded);

  return (
    <Card className="border-slate-200" data-testid="vz-email-exclusions">
      <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><MailX className="w-4 h-4 text-red-500" /> Do not email these stores</CardTitle></CardHeader>
      <CardContent className="space-y-3">
        <p className="text-xs text-slate-500">Stores listed here are left out of the weekly digest, pacing alerts and goal achieved emails for everyone (dealer, partners, RPM/SM and Prime Media CCs). A dealer whose stores are all listed gets no email.</p>
        <div className="relative">
          <Input placeholder="Search store ID, address, city or dealer…" value={q} onChange={(e) => setQ(e.target.value)} className="h-9 text-sm" data-testid="vz-excl-search" />
          {matches.length > 0 && (
            <div className="absolute z-10 mt-1 w-full bg-white border border-slate-200 rounded-md shadow-lg max-h-60 overflow-y-auto">
              {matches.map((s) => (
                <button key={s.store_id} type="button" onClick={() => { setSel([...sel, s.store_id]); setQ(""); }}
                  className="w-full text-left px-3 py-2 text-xs hover:bg-slate-50" data-testid={`vz-excl-add-${s.store_id}`}>
                  <b>{s.store_id}</b> · {s.address}, {s.city} · {s.sub_agent_name}
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="flex flex-wrap gap-2" data-testid="vz-excl-list">
          {sel.length === 0 && <p className="text-xs text-slate-400">No stores excluded.</p>}
          {sel.map((id) => (
            <span key={id} className="inline-flex items-center gap-1 bg-red-50 text-red-700 border border-red-200 rounded-full px-2.5 py-1 text-xs" data-testid={`vz-excl-${id}`}>
              <b>{id}</b>{byId[id] ? ` · ${byId[id].address}` : ""}
              <button type="button" onClick={() => setSel(sel.filter((x) => x !== id))} data-testid={`vz-excl-remove-${id}`}><X className="w-3 h-3" /></button>
            </span>
          ))}
        </div>
        <Button size="sm" onClick={save} disabled={!dirty} data-testid="vz-excl-save"><Save className="w-4 h-4 mr-1" /> Save</Button>
      </CardContent>
    </Card>
  );
};
